import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

function SConsultaStk() {
    const [stock, setStock] = useState([]);
    const [agruparTodos, setAgruparTodos] = useState(true);
    const [depositos, setDepositos] = useState([]);
    const [productos, setProductos] = useState([]);

    // Filtros seleccionados
    const [depositoSeleccionado, setDepositoSeleccionado] = useState(null);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);

    // Estados para cuenta-selector de depósito
    const [dropdownDepositoAbierto, setDropdownDepositoAbierto] = useState(false);
    const [busquedaDeposito, setBusquedaDeposito] = useState('');
    const dropdownDepositoRef = useRef(null);

    // Estados para cuenta-selector de producto
    const [dropdownProductoAbierto, setDropdownProductoAbierto] = useState(false);
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const dropdownProductoRef = useRef(null);

    // Estados para paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(10);

    const API_STOCK_URL = '/api/stock';
    const API_DEPOSITOS_URL = '/api/depositos';
    const API_PRODUCTOS_URL = '/api/productos';

    useEffect(() => {
        cargarDepositos();
        cargarProductos();
        cargarStock();
    }, []);

    useEffect(() => {
        cargarStock();
    }, [agruparTodos, depositoSeleccionado, productoSeleccionado]);

    useEffect(() => {
        setPaginaActual(1);
    }, [stock]);

    // Cerrar dropdowns al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownDepositoRef.current && !dropdownDepositoRef.current.contains(event.target)) {
                setDropdownDepositoAbierto(false);
            }
            if (dropdownProductoRef.current && !dropdownProductoRef.current.contains(event.target)) {
                setDropdownProductoAbierto(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarDepositos = async () => {
        try {
            const response = await axios.get(API_DEPOSITOS_URL);
            setDepositos(response.data);
        } catch (error) {
            console.error('Error al cargar depósitos:', error);
        }
    };

    const cargarProductos = async () => {
        try {
            const response = await axios.get(API_PRODUCTOS_URL);
            setProductos(response.data);
        } catch (error) {
            console.error('Error al cargar productos:', error);
        }
    };

    const cargarStock = async () => {
        try {
            let url;
            let params = {};

            if (agruparTodos) {
                url = `${API_STOCK_URL}/agrupado`;
                if (productoSeleccionado) {
                    params.idProducto = productoSeleccionado.idProducto;
                }
            } else {
                url = `${API_STOCK_URL}/por-deposito`;
                if (depositoSeleccionado) {
                    params.idDeposito = depositoSeleccionado.idDeposito;
                }
                if (productoSeleccionado) {
                    params.idProducto = productoSeleccionado.idProducto;
                }
            }

            const response = await axios.get(url, { params });
            setStock(response.data);
        } catch (error) {
            console.error('Error al cargar stock:', error);
            alert('Error al cargar el stock');
        }
    };

    const handleAgruparChange = (e) => {
        setAgruparTodos(e.target.checked);
        if (e.target.checked) {
            setDepositoSeleccionado(null);
            setBusquedaDeposito('');
        }
    };

    // Funciones para selector de depósito
    const filtrarDepositos = (busqueda) => {
        if (!busqueda) return depositos;
        return depositos.filter(dep =>
            dep.idDeposito?.toString().includes(busqueda) ||
            dep.nomDepto?.toLowerCase().includes(busqueda.toLowerCase())
        );
    };

    const seleccionarDeposito = (deposito) => {
        setDepositoSeleccionado(deposito);
        setBusquedaDeposito(`${deposito.idDeposito} - ${deposito.nomDepto}`);
        setDropdownDepositoAbierto(false);
    };

    const limpiarDeposito = () => {
        setDepositoSeleccionado(null);
        setBusquedaDeposito('');
    };

    // Funciones para selector de producto
    const filtrarProductos = (busqueda) => {
        if (!busqueda) return productos.slice(0, 100);
        return productos.filter(prod =>
            prod.idProducto?.toString().includes(busqueda) ||
            prod.descripcion?.toLowerCase().includes(busqueda.toLowerCase())
        ).slice(0, 100);
    };

    const seleccionarProducto = (producto) => {
        setProductoSeleccionado(producto);
        setBusquedaProducto(`${producto.idProducto} - ${producto.descripcion}`);
        setDropdownProductoAbierto(false);
    };

    const limpiarProducto = () => {
        setProductoSeleccionado(null);
        setBusquedaProducto('');
    };

    // Funciones de paginación
    const indexUltimoItem = paginaActual * itemsPorPagina;
    const indexPrimerItem = indexUltimoItem - itemsPorPagina;
    const itemsActuales = stock.slice(indexPrimerItem, indexUltimoItem);
    const totalPaginas = Math.ceil(stock.length / itemsPorPagina);

    const cambiarPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
    };

    const paginaSiguiente = () => {
        if (paginaActual < totalPaginas) {
            setPaginaActual(paginaActual + 1);
        }
    };

    const paginaAnterior = () => {
        if (paginaActual > 1) {
            setPaginaActual(paginaActual - 1);
        }
    };

    const cambiarItemsPorPagina = (e) => {
        setItemsPorPagina(Number(e.target.value));
        setPaginaActual(1);
    };

    const exportarExcel = () => {
        const datosExportar = stock.map(item => ({
            ...(agruparTodos ? {} : { 'Depósito': item.idDeposito, 'Nombre Depósito': item.nomDepto }),
            'ID Producto': item.idProducto,
            'Descripción': item.descripcion,
            'Grupo': item.nomGrupo,
            'Existencia': item.existencia,
            'Presentación': item.nomPresent
        }));

        const worksheet = XLSX.utils.json_to_sheet(datosExportar);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock');
        XLSX.writeFile(workbook, 'consulta_stock.xlsx');
    };

    const formatearNumero = (valor, decimales = 2) => {
        if (!valor && valor !== 0) return '';
        const numero = parseFloat(valor);
        return numero.toLocaleString(undefined, {
            minimumFractionDigits: decimales,
            maximumFractionDigits: decimales
        });
    };

    return (
        <div className="present-container">
            <div className="header-seccion">
                <h2>Consulta de Stock</h2>
                <div className="acciones-header">
                    <button className="btn btn-exportar" onClick={exportarExcel}>
                        📊 Exportar a Excel
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="filtros-container" style={{
                display: 'flex',
                gap: '20px',
                padding: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                alignItems: 'flex-start'
            }}>
                {/* Checkbox Agrupar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                        type="checkbox"
                        id="agruparTodos"
                        checked={agruparTodos}
                        onChange={handleAgruparChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="agruparTodos" style={{ cursor: 'pointer', fontWeight: '500' }}>
                        Agrupar todos los depósitos
                    </label>
                </div>

                {/* Selector de Depósito (solo si no está agrupado) */}
                {!agruparTodos && (
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                            Depósito:
                        </label>
                        <div className="cuenta-selector" ref={dropdownDepositoRef}>
                            {depositoSeleccionado ? (
                                <div className="cuenta-seleccionada">
                                    <span>{depositoSeleccionado.idDeposito} - {depositoSeleccionado.nomDepto}</span>
                                    <button onClick={limpiarDeposito} className="btn-limpiar">✕</button>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    placeholder="Buscar depósito..."
                                    value={busquedaDeposito}
                                    onChange={(e) => setBusquedaDeposito(e.target.value)}
                                    onFocus={() => setDropdownDepositoAbierto(true)}
                                    className="input-busqueda"
                                />
                            )}

                            {dropdownDepositoAbierto && !depositoSeleccionado && (
                                <div className="cuenta-dropdown">
                                    {filtrarDepositos(busquedaDeposito).map(dep => (
                                        <div
                                            key={dep.idDeposito}
                                            className="cuenta-item"
                                            onClick={() => seleccionarDeposito(dep)}
                                        >
                                            <strong>{dep.idDeposito}</strong> - {dep.nomDepto}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Selector de Producto */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Producto (opcional):
                    </label>
                    <div className="cuenta-selector" ref={dropdownProductoRef}>
                        {productoSeleccionado ? (
                            <div className="cuenta-seleccionada">
                                <span>{productoSeleccionado.idProducto} - {productoSeleccionado.descripcion}</span>
                                <button onClick={limpiarProducto} className="btn-limpiar">✕</button>
                            </div>
                        ) : (
                            <input
                                type="text"
                                placeholder="Buscar producto..."
                                value={busquedaProducto}
                                onChange={(e) => setBusquedaProducto(e.target.value)}
                                onFocus={() => setDropdownProductoAbierto(true)}
                                className="input-busqueda"
                            />
                        )}

                        {dropdownProductoAbierto && !productoSeleccionado && (
                            <div className="cuenta-dropdown">
                                {filtrarProductos(busquedaProducto).map(prod => (
                                    <div
                                        key={prod.idProducto}
                                        className="cuenta-item"
                                        onClick={() => seleccionarProducto(prod)}
                                    >
                                        <strong>{prod.idProducto}</strong> - {prod.descripcion}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabla de resultados */}
            <div className="tabla-scroll">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            {!agruparTodos && <th>Depósito</th>}
                            {!agruparTodos && <th>Nombre Depósito</th>}
                            <th>ID Producto</th>
                            <th>Descripción</th>
                            <th>Grupo</th>
                            <th style={{ textAlign: 'right' }}>Existencia</th>
                            <th>Presentación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {itemsActuales.length === 0 ? (
                            <tr>
                                <td colSpan={agruparTodos ? "5" : "7"} className="sin-datos">
                                    No hay datos para mostrar
                                </td>
                            </tr>
                        ) : (
                            itemsActuales.map((item, index) => (
                                <tr key={index}>
                                    {!agruparTodos && <td>{item.idDeposito}</td>}
                                    {!agruparTodos && <td>{item.nomDepto}</td>}
                                    <td>{item.idProducto}</td>
                                    <td>{item.descripcion}</td>
                                    <td>{item.nomGrupo}</td>
                                    <td style={{ textAlign: 'right' }}>{formatearNumero(item.existencia, 2)}</td>
                                    <td>{item.nomPresent}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Controles de paginación */}
            <div className="paginacion-container" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 20px',
                borderTop: '1px solid #ddd'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label>Mostrar:</label>
                    <select value={itemsPorPagina} onChange={cambiarItemsPorPagina} className="select-items">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <span>por página</span>
                </div>

                <div className="paginacion-info">
                    Mostrando {indexPrimerItem + 1} - {Math.min(indexUltimoItem, stock.length)} de {stock.length} registros
                </div>

                <div className="paginacion-controles" style={{ display: 'flex', gap: '5px' }}>
                    <button
                        onClick={paginaAnterior}
                        disabled={paginaActual === 1}
                        className="btn-paginacion"
                    >
                        ‹ Anterior
                    </button>

                    {[...Array(totalPaginas)].map((_, index) => {
                        const numeroPagina = index + 1;
                        // Mostrar solo algunas páginas alrededor de la actual
                        if (
                            numeroPagina === 1 ||
                            numeroPagina === totalPaginas ||
                            (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
                        ) {
                            return (
                                <button
                                    key={numeroPagina}
                                    onClick={() => cambiarPagina(numeroPagina)}
                                    className={`btn-numero-pagina ${paginaActual === numeroPagina ? 'activo' : ''}`}
                                >
                                    {numeroPagina}
                                </button>
                            );
                        } else if (
                            numeroPagina === paginaActual - 3 ||
                            numeroPagina === paginaActual + 3
                        ) {
                            return <span key={numeroPagina}>...</span>;
                        }
                        return null;
                    })}

                    <button
                        onClick={paginaSiguiente}
                        disabled={paginaActual === totalPaginas}
                        className="btn-paginacion"
                    >
                        Siguiente ›
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SConsultaStk;
